import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import { Box, Typography, Paper, ListItem, ListItemText, Stack, Skeleton, IconButton, Checkbox, FormControlLabel } from '@mui/material';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList as List } from 'react-window';
import { useNavigate } from 'react-router-dom';
import { NotificationContext } from 'contexts';
import { getTranslation } from 'common';
import { useTranslations, useOrganizations } from 'hooks';
import MarkAsUnreadIcon from '@mui/icons-material/MarkAsUnread';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { NotificationContextType } from 'contexts/notificationContext';
import { formatDate } from 'common/utilities';
import NotificationsOrganizationsList from './NotificationsOrganizationsList';
import FlowentiButton from './flowenti-ui-kit/FlowentiButton';
import { Check } from '@mui/icons-material';

type RowProps = {
    index: number;
    style: Object;
};

type NotificationsListProps = {
    compact?: boolean;
};

const NotificationsList: React.FC<NotificationsListProps> = ({ compact = false }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [filterOrgId, setFilterOrgId] = useState<number | null>(null);
    const translations = useTranslations()
    const [organizations] = useOrganizations();
    const navigate = useNavigate();
    const HEADER_HEIGHT = 65;
    const { actions: { readNotification, readAllNotifications, unreadAllNotifications }, state: { notificationList } } = useContext<NotificationContextType>(NotificationContext)

    const readAllNotifs = () => {
        if (!filterOrgId) return;
        readAllNotifications({ idorg: filterOrgId });
    };

    const unreadAllNotifs = () => {
        if (!filterOrgId) return;
        unreadAllNotifications({ idorg: filterOrgId });
    };

    const listRef = useRef<List>(null);
    const allNotifications = useMemo(() => {
        if (filterOrgId === null) return notificationList;
        return notificationList?.filter(n => n.idorganization === filterOrgId);
    }, [notificationList, filterOrgId]);

    const filteredOrgName = useMemo(() => {
        if (filterOrgId === null) return;
        const org = organizations.find(o => o.id === filterOrgId);
        return org?.name ?? "";
    }, [filterOrgId, organizations]);

    //definitely not ai generated function to calculate the height of each item since i could not apply padding to the list item for some reason with this "compact" prop;
    //if u manage to make it work, please just make this function getItemsize ()=>{return 52} and remove calculations
    //p.s. I've checked the numbers, this is decently fine at the moment
    const getItemSize = (index: number): number => {
        if (!compact) return 52;

        const notification = allNotifications[index];
        if (!notification) return 52;

        const charsPerLine = 25;
        const lines = Math.ceil(notification.notificationtext.length / charsPerLine);
        return 70 + (lines - 1) * 20;
    };

    const Row: React.FC<RowProps> = React.memo(({ index, style }) => {
        const notification = allNotifications[index];
        if (!notification) return null;

        return (
            <ListItem
                id={`notif-${String(notification.id)}`}
                data-cy="notification-item"
                divider
                style={{ ...style, cursor: notification.readed ? "default" : "pointer", }}
                onClick={() => {
                    if (!notification.readed) {
                        readNotification({ id: notification.id });
                    }
                }}
                disabled={notification.readed}
            >
                <ListItemText
                    primary={
                        <Typography
                            sx={{ fontWeight: notification.readed ? 'normal' : 'bold' }}
                        >
                            {notification.notificationtext}
                        </Typography>
                    }
                    secondary={
                        <Typography
                        // sx={{ fontWeight: notification.readed ? 'normal' : 'bold' }}
                        >
                            {formatDate(notification.notificationtime)}
                        </Typography>
                    }
                />
                {/*refuso da sistemare */}
                {/* {notification.linkurl && (
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (notification.linkurl.includes('myactivities/task')) {
                                const taskId = notification.linkurl.split('/').pop();
                                console.log(taskId, "processtoken")
                                navigate('/dashboard/myactivities', { state: { taskId: Number(taskId) } });
                            } else {
                                navigate(notification.linkurl);
                            }
                        }}
                    >
                        <ArrowOutwardIcon fontSize="small" />
                    </IconButton>
                )} */}
            </ListItem>
        );
    });

    useEffect(() => {
        setLoading(true)
    }, [])


    return (
        <Stack
            sx={{
                flex: 1,
                maxHeight: `100%`,
                borderRadius: "15px",
            }}
        >
            <Box display="flex" alignItems="center" gap={2} ml={4}>
                <Typography variant="h4">
                    {getTranslation(translations, "menu.left.notifications")}
                </Typography>
                <NotificationsOrganizationsList
                    value={filterOrgId}
                    onChange={setFilterOrgId}
                />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
                <FlowentiButton
                    onClick={readAllNotifs}
                    sx={{ mt: 1, ml: 2, width: "fit-content" }}
                >
                    <MarkEmailReadIcon />
                    <Typography variant="body2">{getTranslation(translations, "generic.notifications.read_all")}</Typography>
                </FlowentiButton>
                <FlowentiButton
                    onClick={unreadAllNotifs}
                    sx={{ mt: 1, ml: 2, width: "fit-content" }}
                >
                    <MarkAsUnreadIcon />
                    <Typography variant="body2">{getTranslation(translations, "generic.notifications.unread_all")}</Typography>
                </FlowentiButton>
            </Box>
            {/* <FormControlLabel
                control={
                    <Checkbox
                        checked={false}
                        onChange={readAllNotifs}
                    />
                }
                label={getTranslation(translations, "generic.notifications.read_all")}
            /> */}
            <Box
                component={Paper}
                sx={{
                    maxHeight: `calc(100% - ${HEADER_HEIGHT}px})`,
                    height: `calc(100% - ${HEADER_HEIGHT}px)`,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    pb: 2,
                    position: "relative",
                    paddingTop: 6
                }}>
                {allNotifications?.length === 0 ?
                    <Box height="100%" display={"flex"} alignItems="center" justifyContent="center">
                        <Typography textAlign={"center"}>
                            {filterOrgId !== null && filteredOrgName
                                ? `${getTranslation(translations, "notifications.no_notifications")} for ${filteredOrgName}`
                                : getTranslation(translations, "notifications.no_notifications")}
                        </Typography>
                    </Box>
                    :
                    <>
                        {loading &&
                            <Box sx={{ px: 2, position: "absolute", inset: 0 }}>
                                {Array.from(Array(10).keys()).map((k) => <Skeleton variant="rectangular" width="100%" height={50} sx={{ borderRadius: 2, my: "5px" }} key={`skeleton-${k}`} />)}
                            </Box>
                        }

                        <AutoSizer style={{ height: `calc(100% - ${HEADER_HEIGHT}px)`, minHeight: 500, width: 100 }}>
                            {({
                                height,
                                width,
                            }: {
                                height: number;
                                width: number;
                            }) => (
                                <List
                                    ref={listRef}
                                    onItemsRendered={() => setLoading(false)}
                                    height={height}
                                    width={width}
                                    itemSize={getItemSize}
                                    itemCount={allNotifications?.length}
                                >
                                    {Row}
                                </List>
                            )}
                        </AutoSizer>
                    </>
                }
            </Box>
        </Stack>
    );
};

export default NotificationsList